import { useEffect, useMemo, useState } from 'react'
import { painColor } from '@pelvia/shared/lib/patient/painColors'
import type {
  PainTrend,
  WeeklyAverage,
  PainTrendRegression,
} from '@pelvia/shared/hooks/patient/usePainPatterns'

const WEEKS_VISIBLE = 6

function dayAgoLabel(dateStr: string): string {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Weekly average-pain bars with a paged 6-week window and a regression
 * summary. Renders nothing until at least one week has been aggregated.
 */
export default function WeeklyTrendCard({ trend }: { trend?: PainTrend }) {
  if (!trend || trend.weekly_averages.length === 0) return null
  return <TrendChart weeks={trend.weekly_averages} regression={trend.regression} />
}

function TrendChart({
  weeks,
  regression,
}: Readonly<{
  weeks: WeeklyAverage[]
  regression: PainTrendRegression | null
}>) {
  const total = weeks.length
  const maxStart = Math.max(0, total - WEEKS_VISIBLE)
  const [startIdx, setStartIdx] = useState(maxStart)
  const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null)

  // If data reshapes (e.g. refetch), keep the window pinned to the most recent weeks.
  useEffect(() => {
    setStartIdx(Math.max(0, total - WEEKS_VISIBLE))
  }, [total])

  const visible = useMemo(
    () => weeks.slice(startIdx, startIdx + WEEKS_VISIBLE),
    [weeks, startIdx],
  )

  const canGoBack = startIdx > 0
  const canGoForward = startIdx < maxStart

  const goBack = () => {
    if (!canGoBack) return
    setSlideDir('right')
    setStartIdx((i) => Math.max(0, i - 1))
  }
  const goForward = () => {
    if (!canGoForward) return
    setSlideDir('left')
    setStartIdx((i) => Math.min(maxStart, i + 1))
  }

  const firstLabel = visible.length ? dayAgoLabel(visible[0].week_start) : ''
  const lastLabel = visible.length ? dayAgoLabel(visible.at(-1)!.week_start) : ''
  const rangeLabel =
    visible.length === 1 ? firstLabel : `${firstLabel} – ${lastLabel}`

  // Use startIdx as the animation key so React re-mounts the bars on each step.
  return (
    <section className="card">
      <div className="pp-trend-header">
        <div>
          <h3 className="pp-card-title">Weekly pain trend</h3>
          <p className="pp-card-desc">
            Showing {visible.length} of {total} week{total > 1 ? 's' : ''} · {rangeLabel}
          </p>
        </div>
        <div className="pp-trend-nav">
          <button
            type="button"
            className="pp-trend-nav-btn"
            onClick={goBack}
            disabled={!canGoBack}
            aria-label="Previous week"
          >
            ‹
          </button>
          <button
            type="button"
            className="pp-trend-nav-btn"
            onClick={goForward}
            disabled={!canGoForward}
            aria-label="Next week"
          >
            ›
          </button>
        </div>
      </div>
      <div
        key={startIdx}
        className={`pp-trend-chart pp-trend-slide-${slideDir ?? 'in'}`}
      >
        {visible.map((w, i) => {
          const h = Math.max(2, (w.avg / 10) * 120)
          return (
            <div
              key={w.week_start}
              className="pp-trend-bar-col"
              style={{ ['--pp-bar-delay' as string]: `${i * 55}ms` }}
            >
              <span className="pp-trend-bar-val">{w.avg}</span>
              <div
                className="pp-trend-bar"
                style={{
                  ['--pp-bar-height' as string]: `${h}px`,
                  background: painColor(w.avg),
                }}
                title={`${w.avg}/10 · ${w.days_logged} days`}
              />
              <div className="pp-trend-label">{dayAgoLabel(w.week_start)}</div>
            </div>
          )
        })}
      </div>
      {regression && (
        <div className={`pp-trend-insight ${regression.direction}`}>
          {regression.direction === 'improving' && '✓ Trending down — '}
          {regression.direction === 'worsening' && '↑ Trending up — '}
          {regression.direction === 'stable' && '→ Stable — '}
          {regression.pct_change > 0 ? '+' : ''}
          {regression.pct_change}% vs your first week.
        </div>
      )}
    </section>
  )
}
